import { useState } from 'react'
import type { JSX } from 'react'
import { Link, useSearchParams } from 'react-router'
import { api, call } from '@/api/client'
import type { components } from '@/api/schema'
import { useMutation, useQuery } from '@/api/query'
import { useBrand } from '@/brand/context'
import { formatDayMonth, relativeTime } from '@/lib/format'
import { summariseSchedule } from '@/settings/run-schedule/summary'
import { Button } from '@/ui/Button'
import { ErrorState, InlineError, Loading } from '@/ui/states'
import { useActiveTenant } from '@/tenant/context'
import { useToast } from '@/ui/toast-context'

type Schedule = components['schemas']['ScheduleOut']
type Scope = Schedule['scope']

/**
 * Run triggers — S-D5's `seo#triggers`, on the real schedule (S-B29).
 *
 * **Three controls, not one "automation level" slider**, because the design's own
 * note says they are three different promises: a schedule is predictable, a manual
 * run is the tenant's own decision, and an automatic run is *"the one that fires
 * while nobody is watching, so it is off by default, every condition names its own
 * threshold"*.
 *
 * **Nothing here knows what a condition means.** The label, the bounds and the
 * default all come off `GET /seo/run-schedules/{id}`, because `app/runs/triggers.CONDITIONS` is
 * where the list and its order are decided — a copy in TypeScript would be the
 * hand-written DTO REQUIREMENTS §9.1 forbids, and it would be the copy that
 * disagreed about which switch exists.
 *
 * THERE IS ONE SPEND CAP, AND IT IS NOT ON THIS SCREEN
 * ------------------------------------------------------
 * The design's third card ends with an input — *"Never spend more than this on
 * automatic runs — ₹2,000/month"*, described as sitting *"inside your ₹8,000
 * monthly cap, not on top of it"*.
 *
 * **SEO-UI-73, answered by the owner on 2026-09-08: no automatic-run cap. The
 * organisation's budget cap is the only one, and it is set by hand at
 * `/settings/budget`.** So the input is not built, and this is a decision about
 * the product rather than a deferral.
 *
 * **Why one cap is the right number.** Two ceilings over one spend is a question
 * the tenant has to answer every time they read either — *which one stopped
 * this, and what is still running under the other* — and the second ceiling buys
 * nothing the first does not already guarantee. `app/runs/executor.py` refuses a
 * stage when the ledger says the ceiling is reached and marks the pass
 * `degraded`, and it does that **whatever triggered the pass**: the organisation
 * cap already stops automatic runs, scheduled ones and the button on this
 * screen. A per-site cap would only ever stop things sooner, and its whole
 * observable effect would be a run that failed for a reason the burn panel does
 * not show.
 *
 * What replaces the input is a sentence in the third card naming the real cap
 * and linking to it, because the drawing raised the question and a screen that
 * simply omitted the control would leave it unanswered.
 *
 * WHY SAVE CAN SUCCEED AND STILL SHOW A WARNING
 * -----------------------------------------------
 * `PUT /seo/run-schedules/{id}` writes the row, then asks EventBridge Scheduler to agree. The
 * second half can fail on its own, and when it does the response is still a 200
 * with `synced: false` — the tenant's setting *did* save. Reporting that as an
 * error would send them to press Save again, which changes nothing. So a failed
 * sync is a warning on the card with the reason on it, and the operator's repair
 * command is what clears it.
 */
export function RunTriggers(): JSX.Element {
  const brand = useBrand()
  const toast = useToast()
  // SF-18. `?site=` is how `/seo/sites` hands over which property was asked
  // about — SHELL-13 took *Run triggers* off the sidebar, so each site's card
  // is now the way in and it has to arrive on that site's schedule. Without it
  // a three-site agency pressing *When AugRank runs* on the second card would
  // land on the first one's conditions, which is the mistake this file's
  // neighbour refuses for the uptime URL. `ReasoningTrail` reads `?run=` the
  // same way. An unknown id falls through to the first site rather than
  // erroring: the picker is on screen, and a stale bookmark is not a failure.
  const [params] = useSearchParams()
  const asked = params.get('site')
  const [siteId, setSiteId] = useState<string | null>(null)

  const sites = useQuery((signal) => call(() => api.GET('/api/v1/seo/sites', { signal })), [])
  const listed = sites.state.data?.sites ?? []
  const requested = listed.some((row) => row.id === asked) ? asked : null
  const selectedId = siteId ?? requested ?? listed[0]?.id ?? null
  const site = listed.find((row) => row.id === selectedId) ?? null

  const schedule = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/run-schedules/{site_id}', {
              params: { path: { site_id: selectedId } },
              signal,
            }),
          ),
    [selectedId],
  )

  const runs = useQuery(
    (signal) =>
      selectedId === null
        ? Promise.resolve({ ok: true as const, data: null })
        : call(() =>
            api.GET('/api/v1/seo/runs', { params: { query: { site_id: selectedId } }, signal }),
          ),
    [selectedId],
  )

  if (sites.state.status === 'loading') return <Loading label="Loading your sites" />
  if (sites.state.status === 'error') {
    return <ErrorState failure={sites.state.failure} onRetry={sites.refetch} />
  }

  return (
    // SEO-UI-105. The heading is **inside** the 46rem column, not above it in a
    // full-width row. `seo.html:1497` wraps the whole screen — h1 and cards — in
    // one `max-width:46rem`, and a heading that starts left of the card it
    // titles reads as belonging to the page rather than to them. The site
    // picker comes with it: SF-26's `.app-page-select` is what keeps the select
    // from claiming the whole line and wrapping the row onto three (SEO-UI-49).
    <div className="app-page-narrow">
      <div className="ar-row">
        <h1 className="ar-h1">When we run</h1>
        <span className="app-spacer" />
        {listed.length > 1 ? (
          <select
            className="ar-select app-page-select"
            aria-label="Which site"
            value={selectedId ?? ''}
            onChange={(event) => {
              setSiteId(event.target.value)
            }}
          >
            {listed.map((row) => (
              <option key={row.id} value={row.id}>
                {row.domain}
              </option>
            ))}
          </select>
        ) : null}
      </div>

      {site === null ? (
        <div className="ar-card">
          <div className="ar-card__body">
            <p className="ar-muted">
              Add a site before setting up when {brand.productName} runs. Nothing can be scheduled
              until there is something to run against.
            </p>
          </div>
        </div>
      ) : schedule.state.status === 'loading' ? (
        <Loading label="Loading this site's schedule" />
      ) : schedule.state.status === 'error' ? (
        <ErrorState failure={schedule.state.failure} onRetry={schedule.refetch} />
      ) : schedule.state.data === null ? null : (
        <TriggerCards
          key={site.id}
          schedule={schedule.state.data}
          activeRunId={runs.state.data?.active?.id ?? null}
          // SEO-UI-74. The most recent run somebody started by hand, off the
          // `GET /seo/runs` this screen already fetches for `active` — so the
          // caption costs no second request. `runs` is newest first
          // (`db/runs.history` orders by `created_at DESC`), so the first
          // `manual` row is the last one.
          lastManualAt={
            runs.state.data?.items.find((row) => row.trigger === 'manual')?.created_at ?? null
          }
          onTriggered={(started) => {
            runs.refetch()
            toast.show({
              kind: 'success',
              title: started ? 'Run started' : 'A run is already going',
              body: started
                ? `${brand.productName} is working on ${site.domain}. The trail will fill in as it goes.`
                : `${brand.productName} is already running on ${site.domain}. This one will finish first.`,
            })
          }}
        />
      )}
    </div>
  )
}

/** The two cards over one site's saved schedule: what is scheduled (read-only,
 *  edited under Settings since 2026-09-15) and *By hand*. Its own component so
 *  that switching site remounts it. */
function TriggerCards({
  schedule,
  activeRunId,
  lastManualAt,
  onTriggered,
}: {
  schedule: Schedule
  activeRunId: string | null
  lastManualAt: string | null
  onTriggered: (started: boolean) => void
}): JSX.Element {
  const brand = useBrand()
  const { tenant } = useActiveTenant()

  const trigger = useMutation((scope: Scope) =>
    call(() =>
      api.POST('/api/v1/seo/sites/{site_id}/runs', {
        params: { path: { site_id: schedule.site_id } },
        body: { scope },
      }),
    ),
  )

  const nextRunAt = schedule.next_run_at ?? null

  return (
    // SEO-UI-105. The 46rem cap moved up to `.app-page-narrow`, which now wraps
    // the heading as well — the prototype caps the whole column, and capping
    // only the cards left the h1 starting left of them.
    <div className="ar-stack ar-stack--loose">
      {/*
        Unattended runs need a live subscription (2026-09-10). The schedule
        fires while nobody is watching, so the notice sits above the card.
        `automation_available` is served on the organisation rather than worked
        out here from `access`: the rule is one function in `app/billing/trial.py`
        and a second copy of it in TypeScript is the thing that would disagree.
      */}
      {tenant.automation_available ? null : (
        <div className="ar-alert ar-alert--warning">
          <span className="ar-alert__icon" aria-hidden="true">
            ◷
          </span>
          <div className="ar-alert__body">
            <strong>Automatic and scheduled runs start when you choose a plan</strong>
            Your trial includes one unattended pass so you can see one run on its own; after that,
            what you set up here begins the moment you subscribe. Runs you start yourself work as
            normal. <Link to="/billing">See the plans</Link>
          </div>
        </div>
      )}

      {/* --- on a schedule, read-only ------------------------------------------
          Settings consolidation (2026-09-15). The schedule and the automatic
          triggers were edited here; they are `/settings/run-schedule` now, with
          the other per-site settings, and this card says what is set and where
          to change it. The link carries the site so the panel opens on it. */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>On a schedule</h2>
          <span className="ar-card__spacer" />
          <Link
            className="ar-btn ar-btn--ghost ar-btn--sm"
            to={`/settings/run-schedule?site=${encodeURIComponent(schedule.site_id)}`}
          >
            Change in Settings →
          </Link>
        </div>
        <div className="ar-card__body ar-stack">
          <p style={{ margin: 0 }}>{summariseSchedule(schedule)}</p>
          {schedule.enabled && !schedule.synced && schedule.sync_error !== null ? (
            <div className="ar-alert ar-alert--warning" role="status">
              <span className="ar-alert__icon" aria-hidden="true">
                !
              </span>
              <div className="ar-alert__body">
                <strong>This schedule is saved but not live yet</strong>
                {brand.productName} could not register it with the scheduler, so it will not fire
                until that is retried. {schedule.sync_error}
              </div>
            </div>
          ) : null}
          {schedule.enabled && nextRunAt !== null ? (
            <p className="ar-subtle" style={{ margin: 0 }}>
              Next run {relativeTime(nextRunAt, new Date())}. Predicted — the scheduler is the
              authority.
            </p>
          ) : null}
        </div>
      </section>

      {/* --- by hand ------------------------------------------------------- */}
      <section className="ar-card">
        <div className="ar-card__header">
          <h2>By hand</h2>
          <span className="ar-card__spacer" />
          <span className="ar-badge ar-badge--success">✓ Always available</span>
        </div>
        <div className="ar-card__body">
          {/* SEO-UI-106. `ar-text-body-sm` and the design's own bottom margin,
              where this had `marginTop: 0` alone — which left the paragraph at
              1rem body size with the UA's `1em` margin under it. */}
          <p className="ar-muted ar-text-body-sm" style={{ margin: '0 0 var(--ar-space-4)' }}>
            &ldquo;Run audit now&rdquo; sits on every site. It cannot be turned off — a tenant who
            has just changed their site should never have to wait for tomorrow&rsquo;s schedule to
            see whether it worked.
          </p>
          {trigger.state.status === 'error' ? (
            <InlineError failure={trigger.state.failure} />
          ) : null}
          <div className="ar-row">
            {/* SEO-UI-107. `--sm` (2rem), as the design draws it. This is a
                secondary action inside a card whose subject is that the button
                is always there, not the screen's primary. */}
            <Button
              tone="ghost"
              size="sm"
              disabled={activeRunId !== null || trigger.pending}
              onClick={() => {
                void trigger.run(schedule.scope).then((result) => {
                  if (!result.ok) return
                  onTriggered(result.data.started)
                })
              }}
            >
              {activeRunId !== null ? 'A run is going…' : 'Run a full pass now'}
            </Button>
            {/* SEO-UI-74. The design's caption is *"Estimated ₹18 · last manual
                run 21 Jul"*, and this carries **the date and not the estimate**.
                The date is real — it is the newest `manual` run in the list this
                screen already holds. The rupee figure is not: nothing joins a
                run to `usage_metering`, which is the same finding
                `ReasoningTrail` records against the prototype's ₹18.40 badge and
                the reason this file already declines to render a spend cap. A
                number that is not derived from anything is worse than no number,
                and it would be worse here than on the trail — this one sits next
                to the button that spends it. */}
            <span className="ar-subtle" style={{ fontSize: 'var(--ar-text-caption-size)' }}>
              {lastManualAt === null
                ? 'No manual run yet'
                : `Last manual run ${formatDayMonth(lastManualAt, schedule.timezone)}`}
            </span>
          </div>
        </div>
      </section>
    </div>
  )
}
